import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import EcCustomFieldTable from '~/components/Shared/EcCustomFieldTable/EcCustomFieldTable';
import { Box } from '~/eds/src/components/Box/Box';
import { CollapsibleCard } from '~/eds/src/components/Card/subcomponents/CollapsibleCard';
import { slices } from '~/redux/slices';

import { DataFieldSection } from './types';

interface Props {
  section: DataFieldSection;
  isCollapsed: boolean;
  onToggle: (isCollapsed: boolean) => void;
  onFieldsModal: (
    modal: string,
    section: DataFieldSection,
    selectedItem: any,
  ) => void;
  onFieldReorder: (
    data: { id: number; position: number }[],
    sectionId: number,
  ) => void;
}
export const SectionCard = memo(
  ({
    section,
    isCollapsed,
    onToggle,
    onFieldsModal,
    onFieldReorder,
  }: Props) => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const canDeleteSection = useMemo(
      () =>
        section.docInfoFields.some(
          (field: any) => !!field.isSmart || field.staticKeyName,
        ),
      [section.docInfoFields],
    );

    return (
      <CollapsibleCard
        mode="bordered"
        header={{
          title: section.label,
          actions: [
            {
              text: intl.formatMessage({
                id: 'DataFields.addDataFieldAction',
                defaultMessage: 'Add Data Field',
                description: 'Add data field action button text',
              }),
              level: 'action',
              onClick: () => {
                dispatch(slices.dataFields.actions.setAction('add-field'));
                dispatch(slices.dataFields.actions.setSection(section));
              },
            },
          ],
          moreActions: [
            {
              text: intl.formatMessage({
                id: 'DataFields.renameAction',
                defaultMessage: 'Rename',
                description: 'Rename action button text',
              }),
              disabled: section.isDefaultSection,
              tooltip: section.isDefaultSection
                ? intl.formatMessage({
                    id: 'DataFields.defaultSectionRenameTooltip',
                    defaultMessage: 'Default sections cannot be renamed',
                    description:
                      'Tooltip text for rename action when the section is a default section',
                  })
                : undefined,
              onClick: () => {
                dispatch(slices.dataFields.actions.setAction('rename'));
                dispatch(slices.dataFields.actions.setSection(section));
              },
            },
            ...(!canDeleteSection
              ? [
                  {
                    text: intl.formatMessage({
                      id: 'DataFields.deleteAction',
                      defaultMessage: 'Delete',
                      description: 'Delete action button text',
                    }),
                    disabled: section.isDefaultSection,
                    tooltip: section.isDefaultSection
                      ? intl.formatMessage({
                          id: 'DataFields.defaultSectionDeleteTooltip',
                          defaultMessage: 'Default sections cannot be deleted',
                          description:
                            'Tooltip text for delete action when the section is a default section',
                        })
                      : undefined,
                    onClick: () => {
                      dispatch(slices.dataFields.actions.setAction('delete'));
                      dispatch(slices.dataFields.actions.setSection(section));
                    },
                  },
                ]
              : []),
          ],
        }}
        togglePosition="left"
        isCollapsed={isCollapsed}
        onToggle={onToggle}
      >
        <Box pt={4}>
          <EcCustomFieldTable
            section={section}
            listItems={section.docInfoFields}
            handleShowModal={onFieldsModal}
            handleFieldReorder={onFieldReorder}
          />
        </Box>
      </CollapsibleCard>
    );
  },
);
